import {
  Card,
  FormField,
  SegmentedControl,
  Select,
  Tag,
} from "@caprikorn/dynamo-ui";
import { type FC, useId } from "react";

import { Header } from "@components/Header/Header";
import { InfoItem } from "@components/InfoItem/InfoItem";
import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { useTheme } from "@contexts/Theme/Theme.context";
import { IS_TEST_ENV, LANGUAGES, LS_KEYS } from "@core/core.constants";
import { useGroupsDropdown } from "@hooks/useGroupsDropdown";
import { Moon, Settings as SettingsIcon, Star, Sun, User03 } from "@icons";
import { ROUTES } from "@router/router.constants";
import { useGetGroup } from "@services/apiEcms/useGetGroup";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import { ResetPasswordForm } from "./ResetPasswordForm/ResetPasswordForm";
import classes from "./Settings.module.css";

export const Settings: FC = () => {
  const titleAccountSettingsId = useId();
  const titlePreferencesSettingsId = useId();
  const themeLabelId = useId();
  const titlePasswordSettingsId = useId();
  const { currentGroup } = useGroupReferences();
  const { t, i18n } = useCustomTranslation(["main", "common"]);
  const { data: groupData, isLoading } = useGetGroup({
    groupId: currentGroup,
  });
  const userEmail = localStorage.getItem(LS_KEYS.EMAIL);
  const { groupsDropdownOptions, groupsOnDropdownChange, groupsDropdownValue } =
    useGroupsDropdown();
  const { themeVariant, switchThemeVariant } = useTheme();

  const isGroupLoaded = groupData && !isLoading;

  return (
    <>
      <Header
        breadcrumbPaths={[
          {
            pathName: t("settings.breadcrumbTitle"),
            path: ROUTES.SETTINGS,
            pathIcon: SettingsIcon,
          },
        ]}
        dropdownOptions={groupsDropdownOptions}
        dropdownValue={groupsDropdownValue}
        onDropdownChange={(select) => groupsOnDropdownChange(select)}
      />

      <div className={classes.container}>
        <div className={classes.row}>
          <Card
            aria-labelledby={titleAccountSettingsId}
            as="section"
            className={classes.card}
          >
            <h3 className="font-title-xl" id={titleAccountSettingsId}>
              {t("settings.account.title")}
            </h3>
            <ul>
              <InfoItem
                name={t("settings.account.email")}
                value={userEmail ?? t("notApplicable", { ns: "common" })}
              />
              {isGroupLoaded && (
                <InfoItem
                  name={t("settings.account.group")}
                  value={
                    groupData?.group.name ??
                    t("notApplicable", { ns: "common" })
                  }
                />
              )}
            </ul>
            {groupData?.isAdmin ? (
              <Tag
                color="contrast"
                icon={Star}
                iconStyle="start"
                label={t("userDetails.role.labels.admin")}
                size="xs"
              />
            ) : (
              <Tag
                color="default"
                icon={User03}
                iconStyle="start"
                label={t("userDetails.role.labels.restricted")}
                size="xs"
              />
            )}
          </Card>
          <Card
            aria-labelledby={titlePreferencesSettingsId}
            as="section"
            className={classes.card}
          >
            <h3 className="font-title-xl" id={titlePreferencesSettingsId}>
              {t("settings.preferences.title")}
            </h3>
            <FormField
              as="div"
              label={t("settings.preferences.theme")}
              labelId={themeLabelId}
            >
              <SegmentedControl
                aria-labelledby={themeLabelId}
                onChange={switchThemeVariant}
              >
                <SegmentedControl.Item
                  icon={Sun}
                  isSelected={themeVariant === "light"}
                  label={t("settings.preferences.themeMode.light")}
                  value="light"
                />

                <SegmentedControl.Item
                  icon={Moon}
                  isSelected={themeVariant === "dark"}
                  label={t("settings.preferences.themeMode.dark")}
                  value="dark"
                />
              </SegmentedControl>
            </FormField>
            {IS_TEST_ENV && (
              <Select
                label={t("settings.preferences.language")}
                onChange={(language) =>
                  language && i18n.changeLanguage(language)
                }
                value={i18n.resolvedLanguage ?? null}
              >
                <Select.Trigger />
                <Select.Dropdown>
                  <Select.Options>
                    {LANGUAGES.map((language) => (
                      <Select.Option
                        key={language}
                        label={t(`languages.${language}`, { ns: "common" })}
                        value={language}
                      />
                    ))}
                  </Select.Options>
                </Select.Dropdown>
              </Select>
            )}
          </Card>
        </div>
        <Card
          aria-labelledby={titlePasswordSettingsId}
          as="section"
          className={classes.card}
        >
          <h3 className="font-title-xl" id={titlePasswordSettingsId}>
            {t("settings.resetPassword.title")}
          </h3>
          <ResetPasswordForm />
        </Card>
      </div>
    </>
  );
};
